/*
 * TerriSTORY®
 *
 © Copyright 2022 AURA-EE
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * A copy of the GNU Affero General Public License should be present along
 * with this program at the root of current repository. If not, see
 * http://www.gnu.org/licenses/.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, Prompt } from "react-router-dom";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { saveAs } from "file-saver";
import Api from "../../Controllers/Api";
import Actions from "./Actions";
import Trajectories from "./Trajectories";
import Impacts from "./Impacts";
import { NewDetailsPopup as DetailsPopup } from "../DetailsPopup";
import { buildRegionUrl, createPdfMethodoLink } from "../../utils";
import config from "../../settings";
import configData from "../../settings_data";
import "../../style/strategies.css";

/**
 * Page for strategy editing. It gets the current strategy id from URL.
 * This page loads data for the whole page in several requests.
 */
export default function StrategyEdition(_ref) {
  var _parentApi$controller, _parentApi$controller2, _parentApi$data$zone, _parentApi$data$setti, _parentApi$data$setti2;
  let {
    parentApi,
    location,
    history
  } = _ref;
  // Data independent of the current status
  const [lastPossibleReferenceYear, setLastPossibleReferenceYear] = useState(0);
  const [trajectoriesMeta, setTrajectoriesMeta] = useState([]);

  // Data dependent on the current zone
  const [actionsMeta, setActionsMeta] = useState([]);
  const [refTrajectories, setRefTrajectories] = useState({});

  // Data dependant on the current strategy
  const [strategy, setStrategy] = useState(undefined);
  const [trajectories, setTrajectories] = useState({});
  const [actionsParams, setActionsParams] = useState([]);
  const [advancedParams, setAdvancedParams] = useState([]);
  const [activeActions, setActiveActions] = useState({});

  // Data created by the component
  const [actionErrors, setActionErrors] = useState({});
  const [impacts, setImpacts] = useState(undefined);
  const [status, setStatus] = useState({});
  const [isLoadingImpacts, setIsLoadingImpacts] = useState(true);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [paramsBackup, setParamsBackup] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showName, setShowName] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /*console.log({ trajectoriesMeta, actionsMeta, refTrajectories, strategy, trajectories,
      actionsParams, advancedParams, activeActions, impacts }); // prettier-ignore*/

  const canStrategyBeModified = (strategy === null || strategy === void 0 ? void 0 : strategy.id) && strategy.mail && strategy.mail === ((_parentApi$controller = parentApi.controller.authManager) === null || _parentApi$controller === void 0 ? void 0 : (_parentApi$controller2 = _parentApi$controller.userInfos) === null || _parentApi$controller2 === void 0 ? void 0 : _parentApi$controller2.id);

  // HACK needed because index.js doesn't use hook useCallback
  const setMainZone = useRef(parentApi.callbacks.updateZone).current;
  const updateProvenance = useRef(parentApi.callbacks.updateProvenance).current;
  useEffect(() => {
    // If we come from strategy creation, show the help popup if the local config enables it
    if (parentApi.data.provenance !== "strategy_creation") return;
    setShowHelp(localStorage.getItem("strategy-hide-help") == null);
    updateProvenance("");
  }, [parentApi.data.provenance, updateProvenance]);
  useEffect(() => {
    if (strategy !== null && strategy !== void 0 && strategy.id && !strategy.partage_par) setHasUnsavedChanges(true);
  }, [strategy, trajectories, actionsParams, advancedParams, activeActions]);
  useEffect(() => {
    if (hasUnsavedChanges) window.onbeforeunload = () => true;
    return () => window.onbeforeunload = undefined;
  }, [hasUnsavedChanges]);
  const {
    region
  } = parentApi.data;
  // Data independent of the current status
  useEffect(() => {
    const tablesProjection = ["DataSet.CONSUMPTION", "DataSet.EMISSIONS", "DataSet.PRODUCTION"];
    const url = buildRegionUrl(config.api_liste_annees_url, region);
    const promises = tablesProjection.map(table => Api.callApi(url.replace("#table#", table)));
    Promise.all(promises).then(responses => {
      const minLastDataYear = Math.min(...responses.map(years => years[years.length - 1].value));
      setLastPossibleReferenceYear(minLastDataYear);
    }).catch(error => {
      if (error.name !== "AbortError") throw error;
    });
    return () => promises.forEach(promise => promise.abort());
  }, [region]);
  const trajMetaUrl = buildRegionUrl(config.api_pcaet_trajectories_list, region);
  Api.useLoadData(trajMetaUrl, setTrajectoriesMeta, console.error);
  useEffect(() => {
    if (!trajectoriesMeta.length) return;
    setTrajectories(trajs => {
      for (const {
        id
      } of trajectoriesMeta) {
        if (trajs[id] === undefined) {
          trajs = {
            ...trajs,
            [id]: {}
          };
        }
      }
      return trajs;
    });
  }, [trajectoriesMeta]);
  Api.useLoadData(buildRegionUrl(config.api_plan_actions_coeffs_url, region), useCallback(defaultParams => {
    setActionsParams(actionsParams => updateIndexedArray(defaultParams, actionsParams, "id"));
  }, []), console.error);

  // Data dependant on the current zone
  const zoneType = (_parentApi$data$zone = parentApi.data.zone) === null || _parentApi$data$zone === void 0 ? void 0 : _parentApi$data$zone.zone;
  const zoneId = parentApi.data.currentZone;
  useEffect(() => {
    if (!zoneType || !zoneId) return;
    let loadActionsMetadataUrl = buildRegionUrl(config.api_plan_actions_actions_url, region) + `?zone=${zoneType}&zone_id=${zoneId}`;
    const promise = Api.callApi(loadActionsMetadataUrl);
    promise.then(_ref2 => {
      let {
        actions
      } = _ref2;
      setActionsMeta(actions);
    }).catch(e => {
      if (e.name === "AbortError") return;
      console.error(e);
    });
    return () => promise.abort();
  }, [region, zoneType, zoneId]);
  const defaultAvancedUrl = buildRegionUrl(config.api_plan_actions_advanced_params_url, region) + `?zone=${zoneType}&zone_id=${zoneId}`;
  Api.useLoadData(defaultAvancedUrl, useCallback(_ref3 => {
    let {
      actions
    } = _ref3;
    setAdvancedParams(advancedParams => updateIndexedArray(actions, advancedParams, "action"));
  }, []), console.error);
  const baseRefTrajectoriesUrl = zoneType && zoneId ? buildRegionUrl(config.api_pcaet_trajectory_details, region) + `?zone=${zoneType}&zone_id=${zoneId}` : undefined;
  useEffect(() => {
    if (!baseRefTrajectoriesUrl) return;
    const promises = trajectoriesMeta.map(meta => Api.callApi(baseRefTrajectoriesUrl.replace("#trajectory_id#", meta.id)));
    Promise.all(promises).then(responses => {
      const refTrajs = Object.fromEntries(responses.map((traj, i) => [trajectoriesMeta[i].id, traj])
      /*traj.filter(({ affectation }) => {
          // Is it needed to check for affectation in front ?
          const affectation = JSON.parse(affectation);
          const isAffectationOK =
              affectation[zoneType + "s"] === "" ||
              affectation[zoneType] === zoneId;
          return isAffectationOK;
      }),*/);
      setRefTrajectories(refTrajs);
    }).catch(error => {
      if (error.name !== "AbortError") throw error;
    });
    return () => promises.forEach(promise => promise.abort());
  }, [trajectoriesMeta, baseRefTrajectoriesUrl]);

  // Data dependant on the current strategy
  useEffect(() => {
    var _location$pathname$ma;
    // If the URL ends by a number, use it as the strategy ID
    const strategyId = (_location$pathname$ma = location.pathname.match(/(?<=\/)[0-9]+$/)) === null || _location$pathname$ma === void 0 ? void 0 : _location$pathname$ma[0];
    if (strategyId == null) return;
    let url = buildRegionUrl(config.user_scenario_url, region);
    url += "/" + strategyId;
    const promise = Api.callApi(url);
    promise.then(value => {
      const {
        actions,
        params,
        advanced,
        trajectoires,
        ...strategy
      } = value;
      if (params !== null && params !== void 0 && params.length && !strategy.referenceYear) {
        const minLastDataYear = Math.min(...params.flatMap(_ref4 => {
          let {
            valeur
          } = _ref4;
          return Object.keys(valeur !== null && valeur !== void 0 ? valeur : {});
        }));
        if (isFinite(minLastDataYear)) {
          strategy.referenceYear = minLastDataYear - 1;
        }
      }
      setStrategy(strategy);
      const [zoneType, zoneMaille] = strategy.zone_type.split("#");
      setMainZone(zoneType, zoneMaille !== null && zoneMaille !== void 0 ? zoneMaille : zoneType, strategy.zone_id);
      const {
        energie_economisee,
        energie_produite,
        ...otherTrajs
      } = trajectoires;
      setTrajectories({
        consommation_ener: energie_economisee,
        enr_production: energie_produite,
        ...otherTrajs
      });
      convertParamsToNumber(params);
      setActionsParams(actionsParams => updateIndexedArray(actionsParams, params, "id"));
      setAdvancedParams(advancedParams => updateIndexedArray(advancedParams, advanced, "action"));
      const filledActions = {};
      for (const param of params) {
        filledActions[param.action] |= param.valeur && Object.keys(param.valeur).length > 0;
      }
      const activeActions = Object.fromEntries(Object.keys(filledActions).filter(a => filledActions[a]).map(a => [a, actions.includes(a)]));
      setActiveActions(activeActions);
    }).catch(error => {
      if (error.name === "AbortError") return;
      console.error(error);
      setStatus({
        type: "error",
        message: error.message
      });
    });
    return () => promise.abort();
  }, [location, region, setMainZone]);
  useEffect(() => {
    if (!lastPossibleReferenceYear) return;
    if ((strategy === null || strategy === void 0 ? void 0 : strategy.referenceYear) === lastPossibleReferenceYear) return;

    // If the strategy is loaded but doesn't have a reference year
    // Set it to the last year where all indicators are available
    if (strategy !== null && strategy !== void 0 && strategy.referenceYear && strategy.referenceYear < lastPossibleReferenceYear) {
      alert("Cette stratégie est construite sur des données obsolètes et pourrait" + " ne plus fonctionner convenablement. Nous vous invitons à en" + " recréer une nouvelle, pour profiter de données actualisées.");
    }
    setStrategy(strategy => {
      return {
        ...(strategy !== null && strategy !== void 0 ? strategy : {}),
        referenceYear: lastPossibleReferenceYear
      };
    });
  }, [lastPossibleReferenceYear, strategy]);
  useEffect(() => {
    if (!(actionsParams !== null && actionsParams !== void 0 && actionsParams.length) || !(actionsMeta !== null && actionsMeta !== void 0 && actionsMeta.length)) return;
    const filledActions = {};
    for (const action of actionsMeta) {
      filledActions[action.numero] = actionsParams.some(param => param.action === action.numero && param.valeur && Object.keys(param.valeur).length > 0);
    }
    setActiveActions(oldActions => {
      const newActions = {
        ...oldActions
      };
      let hasChanged = false;
      for (const actionNb in filledActions) {
        if (filledActions[actionNb] && newActions[actionNb] === undefined) {
          newActions[actionNb] = hasChanged = true;
        }
        if (!filledActions[actionNb] && newActions[actionNb] !== undefined) {
          delete newActions[actionNb];
          hasChanged = true;
        }
      }
      return hasChanged ? newActions : oldActions;
    });
  }, [actionsParams, actionsMeta]);
  function saveStrategy(strategyToSave, isNew) {
    var _strategyToSave, _strategyToSave2;
    strategyToSave = (_strategyToSave = strategyToSave) !== null && _strategyToSave !== void 0 ? _strategyToSave : strategy;
    if (!((_strategyToSave2 = strategyToSave) !== null && _strategyToSave2 !== void 0 && _strategyToSave2.id) && !isNew) {
      console.error("Il n'y a pas de stratégie territoriale active");
      return;
    }
    setIsLoadingSave(true);
    const {
      consommation_ener,
      enr_production,
      ...otherTrajs
    } = trajectories;
    const strategyToUpload = {
      titre: strategyToSave.titre,
      description: strategyToSave.description,
      referenceYear: strategyToSave.referenceYear,
      trajectoires: {
        energie_economisee: consommation_ener,
        energie_produite: enr_production,
        ...otherTrajs
      },
      actions: Object.keys(activeActions).filter(nb => activeActions[nb]),
      advanced: advancedParams
    };
    if (isNew) {
      strategyToUpload.zone_type = parentApi.data.zone.zone;
      strategyToUpload.zone_id = parentApi.data.currentZone;
      strategyToUpload.nom_territoire = parentApi.controller.zonesManager.getZoneName(parentApi.data.currentZone, parentApi.data.zone);
    }
    actionsParams.forEach(action => {
      if (action.nom && action.valeur && Object.keys(action.valeur).length > 0) {
        strategyToUpload[action.nom] = action.valeur;
      }
    });
    let url = buildRegionUrl(config.user_scenario_url, region);
    if (!isNew) url += "/" + strategyToSave.id;
    let method = isNew ? "POST" : "PUT";
    Api.callApi(url, JSON.stringify(strategyToUpload), method).then(response => {
      setIsLoadingSave(false);
      setHasUnsavedChanges(false);
      if (isNew) {
        history.push("/edition_strategie/" + response.scenario_id);
      } else {
        setStatus({
          type: "success",
          message: "Stratégie territoriale enregistrée !"
        });
        setTimeout(() => setStatus({}), 5000);
      }
    }).catch(error => {
      setIsLoadingSave(false);
      setStatus({
        type: "error",
        message: error.message
      });
      console.error(error);
    });
  }
  useEffect(() => setIsLoadingImpacts(actionsMeta.length === 0), [actionsMeta]);
  function computeImpacts() {
    var _parentApi$controller3;
    setIsLoadingImpacts(true);
    setStatus({});
    setParamsBackup(JSON.stringify({
      activeActions,
      actionsParams,
      advancedParams
    }));
    const url = buildRegionUrl(config.api_plan_actions_running_plan, parentApi.data.region) + `?zone=${parentApi.data.zone.zone}` + `&zone_id=${parentApi.data.currentZone}` + `&id_utilisateur=${(_parentApi$controller3 = parentApi.controller.gestionSuiviConsultations) === null || _parentApi$controller3 === void 0 ? void 0 : _parentApi$controller3.idUtilisateur}`;
    const processedActionParams = processStrategyForImpactCalculation(strategy, actionsMeta, activeActions, actionsParams, advancedParams);
    const strategyToUpload = {
      actions: Object.keys(activeActions).filter(nb => activeActions[nb]),
      advanced: advancedParams,
      ...processedActionParams
    };
    Api.callApi(url, JSON.stringify(strategyToUpload), "POST").then(response => {
      setIsLoadingImpacts(false);
      if (response.failed_actions) {
        // TODO TOFIX
        let disabledActions = response.failed_actions.map(a => {
          return a.action_key;
        });
        let actions = [...this.state.actions];
        for (const i in actions) {
          if (disabledActions.includes(actions[i].numero)) {
            actions[i].enabled = false;
          }
        }
        const warningDisabledActions = /*#__PURE__*/React.createElement(DetailsPopup, {
          title: "Actions d\xE9sactiv\xE9es au cours de l'ex\xE9cution",
          isOpen: true,
          onClose: () => setStatus({})
        }, /*#__PURE__*/React.createElement("p", null, "Les actions suivantes ont \xE9t\xE9 automatiquement d\xE9sactiv\xE9es \xE0 cause d'une erreur survenue au cours de l'ex\xE9cution. Si l'erreur se reproduit, merci de transmettre les informations et les d\xE9tails de la situation \xE0 un administrateur."), /*#__PURE__*/React.createElement("ul", null, response.failed_actions.map(a => /*#__PURE__*/React.createElement("li", {
          key: a.action_key
        }, a.name))));
        setStatus({
          type: "raw",
          message: warningDisabledActions
        });
      } else {
        // Store results
        const {
          energie_economisee,
          energie_produite,
          ...otherImpacts
        } = response;
        setImpacts({
          consommation_ener: energie_economisee,
          enr_production: energie_produite,
          ...otherImpacts
        });
      }
    }).catch(error => {
      if (error.name === "AbortError") return;
      setIsLoadingImpacts(false);
      setStatus({
        type: "error",
        message: error.message
      });
      console.error(error);
    });
  }
  async function exportStrategy(format) {
    var _parentApi$controller4;
    let url, fileName;
    if (format === "pcaet") {
      url = config.api_export_strategy_ademe;
      fileName = "export-pcaet-ademe.xlsx";
    } else if (format === "impacts") {
      url = config.api_export_strategy_results;
      fileName = "export-impacts.xlsx";
    }
    if (url === undefined) throw Error("Unknown export format");
    url = buildRegionUrl(url, region) + `?zone=${parentApi.data.zone.zone}` + `&zone_id=${parentApi.data.currentZone}` + `&id_utilisateur=${(_parentApi$controller4 = parentApi.controller.gestionSuiviConsultations) === null || _parentApi$controller4 === void 0 ? void 0 : _parentApi$controller4.idUtilisateur}`;
    let strategyToUpload;
    if (format === "impacts") {
      let customTraj = Object.fromEntries(Object.entries(trajectories).map(_ref5 => {
        let [key, {
          annees_valeurs,
          annees_modifiees
        }] = _ref5;
        if (key === "consommation_ener") key = "energie_economisee";
        if (key === "enr_production") key = "energie_produite";
        return [key, {
          annees: Object.keys(annees_valeurs),
          valeurs: Object.values(annees_valeurs),
          annees_modifiees: Object.values(annees_modifiees)
        }];
      }));
      let target = {};
      for (const id in refTrajectories) {
        var _refTrajectories$id$p;
        if ((_refTrajectories$id$p = refTrajectories[id].pcaet_trajectory) !== null && _refTrajectories$id$p !== void 0 && _refTrajectories$id$p.length) {
          const traj = refTrajectories[id].pcaet_trajectory;
          const yearsList = traj[0].data.map(_ref6 => {
            let {
              annee
            } = _ref6;
            return annee;
          });
          for (const year of yearsList) {
            // Sum all categories corresponding to current year to get total value
            const value = traj.map(_ref7 => {
              var _data$find$valeur, _data$find;
              let {
                data
              } = _ref7;
              return (_data$find$valeur = (_data$find = data.find(_ref8 => {
                let {
                  annee
                } = _ref8;
                return annee === year;
              })) === null || _data$find === void 0 ? void 0 : _data$find.valeur) !== null && _data$find$valeur !== void 0 ? _data$find$valeur : 0;
            }).reduce((a, b) => a + b, 0);
            target[id + "_annee_" + year] = value;
          }
        }
      }
      strategyToUpload = {
        parameters: {
          actions: actionsMeta.map(meta => ({
            ...meta,
            enabled: activeActions[meta.numero]
          })),
          coeffs: actionsParams.map(_ref9 => {
            let {
              valeur,
              ...param
            } = _ref9;
            return {
              ...param,
              valeur_annee: valeur !== null && valeur !== void 0 ? valeur : {}
            };
          }),
          coeffsAdvanced: advancedParams
        },
        results: (() => {
          const {
            consommation_ener,
            enr_production,
            ...otherImpacts
          } = impacts;
          return {
            energie_economisee: consommation_ener,
            energie_produite: enr_production,
            ...otherImpacts
          };
        })(),
        trajectories: {
          custom: customTraj,
          pcaet: Object.keys(target).length > 0,
          target,
          target_ref: (() => {
            const {
              consommation_ener,
              enr_production,
              ...otherTrajs
            } = refTrajectories;
            return Object.fromEntries(Object.entries({
              energie_economisee: consommation_ener,
              energie_produite: enr_production,
              ...otherTrajs
            }).map(_ref10 => {
              let [key, {
                supra_goals
              }] = _ref10;
              return [key, supra_goals];
            }));
          })()
        },
        title: strategy.titre
      };
    } else {
      const processedActionParams = processStrategyForImpactCalculation(strategy, actionsMeta, activeActions, actionsParams, advancedParams);
      strategyToUpload = {
        actions: Object.keys(activeActions).filter(nb => activeActions[nb]),
        advanced: advancedParams,
        title: strategy.titre,
        ...processedActionParams
      };
    }
    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(strategyToUpload),
        credentials: "include"
      });
      if (!response.ok) {
        throw Error(await response.text());
      }
      saveAs(await response.blob(), fileName);
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message
      });
      console.error(error);
    }
  }
  function renameStrategy(titre, isNew) {
    setStrategy(prevState => ({
      ...prevState,
      titre
    }));
    saveStrategy({
      ...strategy,
      titre
    }, isNew);
  }
  const actionsHaveChanged = JSON.stringify({
    activeActions,
    actionsParams,
    advancedParams
  }) !== paramsBackup;
  const zoneName = parentApi.controller.zonesManager ? parentApi.controller.zonesManager.getZoneName(parentApi.data.currentZone, parentApi.data.zone) : "(inconnu)";
  let returnButton = /*#__PURE__*/React.createElement(Link, {
    className: "back-to-map",
    to: "/strategies_territoriales"
  }, /*#__PURE__*/React.createElement("span", {
    className: "big"
  }, "<"), " ", /*#__PURE__*/React.createElement("span", null, "Retour aux strat\xE9gies"));
  if ((_parentApi$data$setti = parentApi.data.settings) !== null && _parentApi$data$setti !== void 0 && _parentApi$data$setti.is_national) {
    returnButton = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Link, {
      className: "btn btn-light return-button",
      to: "/national/" + region + "/portail"
    }, /*#__PURE__*/React.createElement("i", {
      className: "bi bi-arrow-left"
    })));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "widgets full-screen-widget"
  }, returnButton, /*#__PURE__*/React.createElement("div", {
    className: "ui-main"
  }, /*#__PURE__*/React.createElement("button", {
    className: "help strategy-help-button",
    onClick: () => setShowHelp(true)
  }, "Aide"), /*#__PURE__*/React.createElement("h1", {
    className: "tstitle left"
  }, "\xC9laborer une strat\xE9gie", (strategy === null || strategy === void 0 ? void 0 : strategy.titre) && " : " + strategy.titre), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("b", null, "Territoire"), " : ", zoneName), /*#__PURE__*/React.createElement("div", {
    className: "strategy-edit-buttons"
  }, canStrategyBeModified && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    className: "tsbtn info",
    onClick: () => saveStrategy(),
    disabled: isLoadingSave
  }, isLoadingSave && /*#__PURE__*/React.createElement("span", {
    className: "spinner-border align-middle"
  }), " ", "Sauvegarder"), /*#__PURE__*/React.createElement("button", {
    className: "tsbtn",
    onClick: () => setShowName("rename")
  }, "Renommer")), !canStrategyBeModified && parentApi.data.connected && /*#__PURE__*/React.createElement("button", {
    className: "tsbtn info",
    onClick: () => setShowName("new"),
    disabled: isLoadingSave
  }, isLoadingSave && /*#__PURE__*/React.createElement("span", {
    className: "spinner-border"
  }), strategy !== null && strategy !== void 0 && strategy.id ? "Dupliquer" : "Sauvegarder"), /*#__PURE__*/React.createElement("button", {
    className: "tsbtn",
    onClick: () => setShowExport(true)
  }, "Exporter..."), !(strategy !== null && strategy !== void 0 && strategy.id) && !parentApi.data.connected && /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("i", {
    className: "bi bi-info-circle"
  }), " Connectez-vous afin de sauvegarder votre strat\xE9gie")), (status === null || status === void 0 ? void 0 : status.type) === "raw" ? status.message : (status === null || status === void 0 ? void 0 : status.type) === "error" ? /*#__PURE__*/React.createElement("div", {
    className: "alert alert-warning"
  }, status.message) : (status === null || status === void 0 ? void 0 : status.type) === "success" ? /*#__PURE__*/React.createElement("div", {
    className: "alert alert-success"
  }, status.message) : null, /*#__PURE__*/React.createElement(Tabs, null, /*#__PURE__*/React.createElement(TabList, {
    className: "tabs-primary"
  }, /*#__PURE__*/React.createElement(Tab, {
    title: "D\xE9finir les trajectoires cibles"
  }, /*#__PURE__*/React.createElement("span", null, "Trajectoires")), /*#__PURE__*/React.createElement(Tab, {
    title: "Param\xE9trer les actions mises en \u0153uvre"
  }, /*#__PURE__*/React.createElement("span", null, "Actions")), /*#__PURE__*/React.createElement(Tab, {
    title: "Visualiser les impacts des actions"
  }, /*#__PURE__*/React.createElement("span", null, "Impacts"))), /*#__PURE__*/React.createElement(TabPanel, null, /*#__PURE__*/React.createElement(Trajectories, {
    parentApi: parentApi,
    strategy: strategy,
    trajectoriesMeta: trajectoriesMeta,
    refTrajectories: refTrajectories,
    trajectories: trajectories,
    setTrajectories: setTrajectories
  })), /*#__PURE__*/React.createElement(TabPanel, null, /*#__PURE__*/React.createElement(Actions, {
    strategy: strategy,
    actionsMeta: actionsMeta,
    params: actionsParams,
    setParams: setActionsParams,
    advancedParams: advancedParams,
    setAdvancedParams: setAdvancedParams,
    activeActions: activeActions,
    setActionErrors: setActionErrors
  })), /*#__PURE__*/React.createElement(TabPanel, null, /*#__PURE__*/React.createElement(Impacts, {
    parentApi: parentApi,
    strategy: strategy,
    actionsMeta: actionsMeta,
    activeActions: activeActions,
    setActiveActions: setActiveActions,
    computeImpacts: computeImpacts,
    impacts: impacts,
    trajectoriesMeta: trajectoriesMeta,
    refTrajectories: refTrajectories,
    trajectories: trajectories,
    isLoadingImpacts: isLoadingImpacts,
    zoneName: zoneName,
    actionsHaveChanged: actionsHaveChanged,
    actionErrors: actionErrors
  })))), /*#__PURE__*/React.createElement(HelpPopup, {
    parentApi: parentApi,
    isOpen: showHelp,
    onClose: () => setShowHelp(false)
  }), /*#__PURE__*/React.createElement(ExportPopup, {
    isOpen: showExport,
    onClose: () => setShowExport(false),
    onValidate: exportStrategy,
    isImpactAllowed: impacts !== undefined,
    showResultsExport: (_parentApi$data$setti2 = parentApi.data.settings) === null || _parentApi$data$setti2 === void 0 ? void 0 : _parentApi$data$setti2.export_excel_results
  }), /*#__PURE__*/React.createElement(RenamePopup, {
    isOpen: showName,
    onClose: () => setShowName(false),
    onValidate: name => renameStrategy(name, showName === "new"),
    defaultName: strategy === null || strategy === void 0 ? void 0 : strategy.titre
  }), /*#__PURE__*/React.createElement(Prompt, {
    when: hasUnsavedChanges,
    message: "Les changements que vous avez effectu\xE9s pourraient ne pas \xEAtre sauvegard\xE9s. Voulez-vous continuer ?"
    //message="Des données que vous avez saisies pourraient ne pas être enregistrées. Quitter la page ?"
  }));
}
function HelpPopup(_ref11) {
  let {
    parentApi,
    isOpen,
    onClose
  } = _ref11;
  const pdfUrl = createPdfMethodoLink(config.methodo_url, parentApi.data.region, configData.methodoPlanActionsPdf);
  return /*#__PURE__*/React.createElement(DetailsPopup, {
    title: "\xC9laborer une strat\xE9gie",
    className: "large strategy-help",
    isOpen,
    onClose
  }, /*#__PURE__*/React.createElement("p", null, "Pour \xE9laborer votre strat\xE9gie, nous vous proposons une d\xE9marche en trois temps\xA0:"), /*#__PURE__*/React.createElement("ol", {
    className: "steps"
  }, /*#__PURE__*/React.createElement("li", null, "D\xE9finissez vos TRAJECTOIRES cibles (objectifs PCAET)"), /*#__PURE__*/React.createElement("li", null, "Choisissez et param\xE9trez les ACTIONS que vous souhaitez mettre en \u0153uvre"), /*#__PURE__*/React.createElement("li", null, "Visualisez les IMPACTS de vos actions relativement \xE0 vos trajectoires cibles")), /*#__PURE__*/React.createElement("div", {
    className: "center-block"
  }, /*#__PURE__*/React.createElement("h3", null, "Avant de commencer"), /*#__PURE__*/React.createElement("p", null, "Nous vous invitons \xE0 pr\xE9parer les \xE9l\xE9ments suivants\xA0:"), /*#__PURE__*/React.createElement("ul", null, /*#__PURE__*/React.createElement("li", null, "Les ambitions chiffr\xE9es de votre collectivit\xE9 \xE0 horizon 2050 en mati\xE8re d'\xE9nergie consomm\xE9e, d'EnR produite et d'\xE9mission de GES."), /*#__PURE__*/React.createElement("li", null, "Toutes les donn\xE9es chiffr\xE9es en votre possession concernant les plans d'actions envisag\xE9s par votre collectivit\xE9 sur les th\xE9matiques de b\xE2timent, mobilit\xE9.")), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("p", null, "Des donn\xE9es manquantes ?"), /*#__PURE__*/React.createElement("p", null, "Pas de panique ! Vous n'\xEAtes pas oblig\xE9 de tout compl\xE9ter et vous pourrez revenir sur votre strat\xE9gie ult\xE9rieurement pour la compl\xE9ter.")), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("p", {
    style: {
      textAlign: "center"
    }
  }, /*#__PURE__*/React.createElement("a", {
    href: pdfUrl
  }, "Fiche m\xE9thodologique Strat\xE9gies Territoriales")), /*#__PURE__*/React.createElement("div", {
    className: "check-input"
  }, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    defaultChecked: localStorage.getItem("strategy-hide-help") == null,
    onChange: event => event.target.checked ? localStorage.removeItem("strategy-hide-help") : localStorage.setItem("strategy-hide-help", ""),
    id: "show-help-by-default"
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: "show-help-by-default"
  }, "Afficher \xE0 la cr\xE9ation d\u2019une strat\xE9gie territoriale")), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("button", {
    className: "tsbtn info big",
    style: {
      display: "block",
      margin: "auto"
    },
    onClick: onClose
  }, "Ok"));
}
function ExportPopup(_ref12) {
  let {
    isOpen,
    onClose,
    onValidate,
    isImpactAllowed,
    showResultsExport = false
  } = _ref12;
  const [isLoading, setLoading] = useState(false);
  return /*#__PURE__*/React.createElement(DetailsPopup, {
    title: "Exporter une strat\xE9gie",
    isOpen,
    onClose
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: async event => {
      event.preventDefault();
      setLoading(true);
      await onValidate(event.target.elements["format"].value);
      setLoading(false);
      onClose();
    },
    className: "full-screen-form"
  }, /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "check-input"
  }, /*#__PURE__*/React.createElement("input", {
    type: "radio",
    name: "format",
    value: "pcaet",
    id: "format-pcaet",
    required: true
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: "format-pcaet"
  }, "Export Excel PCAET au format du cadre de d\xE9p\xF4t ADEME")), /*#__PURE__*/React.createElement("div", {
    className: "check-input" + (isImpactAllowed && showResultsExport ? "" : " disabled")
  }, /*#__PURE__*/React.createElement("input", {
    type: "radio",
    name: "format",
    value: "impacts",
    id: "format-impacts",
    required: true,
    disabled: !isImpactAllowed || !showResultsExport
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: "format-impacts",
    title: showResultsExport ? isImpactAllowed ? undefined : "Veuillez calculer les impacts afin de les exporter" : "Fonctionnalité désactivée"
  }, "Export Excel des impacts"))), /*#__PURE__*/React.createElement("input", {
    type: "submit",
    className: "tsbtn info big full-width loading",
    value: "Exporter",
    disabled: isLoading
  })));
}
function RenamePopup(_ref13) {
  let {
    isOpen,
    onClose,
    onValidate,
    defaultName
  } = _ref13;
  return /*#__PURE__*/React.createElement(DetailsPopup, {
    title: isOpen === "new" ? "Nouvelle stratégie" : "Renommer une stratégie",
    isOpen,
    onClose
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: async event => {
      event.preventDefault();
      onValidate(event.target.elements["name"].value || "Sans titre");
      onClose();
    },
    className: "full-screen-form"
  }, /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement("div", null, " ", /*#__PURE__*/React.createElement("h2", null, "Nom de la strat\xE9gie"), /*#__PURE__*/React.createElement("input", {
    type: "text",
    name: "name",
    placeholder: "Sans titre",
    className: "full-width",
    defaultValue: defaultName
  })), /*#__PURE__*/React.createElement("input", {
    type: "submit",
    className: "tsbtn info big full-width",
    value: "Sauvegarder"
  })));
}
function processStrategyForImpactCalculation(strategy, actionsMeta, activeActions, actionsParams, advancedParams) {
  const years = [];
  for (let y = strategy.referenceYear + 1; y <= configData.planActionDerniereAnnee; y++) {
    years.push(y);
  }
  const defaultParam = Object.fromEntries(years.map(year => [year, 0]));
  const params = {
    economique: {}
  };
  const actions = {};
  const advanced = {};
  const yearAdvancedParams = {};
  for (let actionNb in activeActions) {
    var _advancedParams$find;
    if (!activeActions[actionNb]) continue;
    const action = actionsMeta.find(_ref14 => {
      let {
        numero
      } = _ref14;
      return numero === actionNb;
    });
    if (!action) {
      console.warn(`Skipping action nb ${actionNb}: not found in actionsMeta.`);
      continue;
    }
    for (const param of actionsParams) {
      // we skip the parameters of other actions
      if (param.action !== action.numero) continue;
      // Fill the actions activated years
      for (const year of years) {
        if (!actions[year]) actions[year] = [];
        if (!actions[year].includes(action.numero)) {
          actions[year].push(action.numero);
        }
      }
      params[param.nom] = {
        ...defaultParam,
        ...param.valeur
      };
    }
    const advancedParam = (_advancedParams$find = advancedParams.find(param => param.action === action.numero)) === null || _advancedParams$find === void 0 ? void 0 : _advancedParams$find.params_avances;
    if (advancedParam !== null && advancedParam !== void 0 && advancedParam.economique) {
      // Economic
      for (let phase of advancedParam.economique) {
        // get the value (proportion)
        let value = phase.part;
        if (value == null || value === "") {
          throw Error("Merci de renseigner une valeur dans tous les paramètres économiques de l'action : " + action.name);
        }
        params.economique[phase.id] = Number(value);
      }
    }
    if (advancedParam !== null && advancedParam !== void 0 && advancedParam.autres) {
      // Others
      advanced[action.numero] = {};
      for (let subkey in advancedParam.autres) {
        let paramsAutres = {};
        for (let i in advancedParam.autres[subkey].index) {
          let idxValeur = advancedParam.autres[subkey].columns.indexOf("valeur");
          let idxName = advancedParam.autres[subkey].columns.indexOf("nom");
          let value = advancedParam.autres[subkey].data[i][idxValeur];
          if (value == null || value === "") {
            throw Error("Merci de renseigner une valeur dans tous les paramètres avancés de l'action : " + action.name);
          }
          if (idxName !== -1) {
            paramsAutres[advancedParam.autres[subkey].data[i][idxName]] = Number(value);
          } else {
            paramsAutres[advancedParam.autres[subkey].index[i]] = Number(value);
          }
        }
        advanced[action.numero][subkey] = paramsAutres;
      }
    }
    if (advancedParam !== null && advancedParam !== void 0 && advancedParam.others_years) {
      // Yearly params
      yearAdvancedParams[action.numero] = {};
      for (let subkey in advancedParam.others_years) {
        const param = advancedParam.others_years[subkey];
        let otherYearParams = {};
        // We have to get all fields (years)
        let advancedValues = {};
        for (let year in years) {
          var _param$valeur_annee$y;
          let value = String((_param$valeur_annee$y = param.valeur_annee[year]) !== null && _param$valeur_annee$y !== void 0 ? _param$valeur_annee$y : 0);
          if (value.trim() !== "") {
            advancedValues[year] = value;
          }
        }
        // add the reference year if not exists
        if (!Object.keys(advancedValues).includes(action.annee_ref)) {
          advancedValues[action.annee_ref] = String(0);
        }
        otherYearParams[param.index[0]] = advancedValues;
        yearAdvancedParams[action.numero][subkey] = otherYearParams;
      }
    }
  }
  params.actions = actions;
  params.advanced = advanced;
  params.yearAdvancedParams = yearAdvancedParams;
  params.trajectoires_cibles = []; // TODO trajectoires activées ou non (info pour la mesure d'audience)

  return params;
}
function updateIndexedArray(original, newValues, key) {
  // Instead of updating the original array, we put all new values, then put missing values from the original array
  const newArray = [...newValues];
  for (const value of original) {
    if (!newArray.find(val => val[key] === value[key])) {
      newArray.push(value);
    }
  }
  return newArray;
}
function convertParamsToNumber(params) {
  for (const param of params) {
    if (!param.valeur) continue;
    const siblings = params.filter(_ref15 => {
      let {
        action
      } = _ref15;
      return action === param.action;
    });
    for (const [year, value] of Object.entries(param.valeur)) {
      param.valeur[year] = parseFloat(value);
      if (siblings.every(_ref16 => {
        let {
          valeur
        } = _ref16;
        return !(valeur !== null && valeur !== void 0 && valeur[year]) || valeur[year] === "0";
      })) {
        delete param.valeur[year];
      }
    }
  }
  return params;
}